import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { type ComponentPropsWithoutRef, type ReactNode, forwardRef } from 'react';
import { cn } from '../../lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
});

interface DialogContentProps extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const DialogContent = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent({ className, size = 'md', children, ...props }, ref) {
  const widthClass = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
  }[size];
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // `flex flex-col` + a viewport-relative max-height keeps tall dialogs
          // (e.g. the Eyewitness review with full text + media gallery + meta
          // grid) from clipping their title or footer off-screen on small
          // viewports. Callers wrap their middle content in something with
          // `flex-1 overflow-y-auto` to make the body the scroll region;
          // header/footer stay pinned via the flex layout.
          // `100dvh` follows the visible viewport on mobile browsers (excludes
          // the auto-hiding URL bar), so the cap matches what the user sees.
          'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-hidden rounded-lg border border-line bg-surface p-6 shadow-popover data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          widthClass,
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-md text-ink-subtle opacity-70 transition hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-brand/30"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

export function DialogHeader({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('flex flex-col gap-1', className)}>{children}</div>;
}

export function DialogFooter({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}>
      {children}
    </div>
  );
}

/**
 * Scrolling middle section for a Dialog. Wrap the body content in this so
 * `DialogContent`'s flex layout works as advertised: header stays pinned at
 * the top, footer stays pinned at the bottom, and this region takes the
 * remaining space and scrolls internally when the content is taller than
 * the viewport can accommodate.
 *
 * Without a `DialogBody`, `DialogContent`'s `overflow-hidden` clips the
 * form's Save/Cancel buttons on short viewports (mobile browsers with the
 * URL bar visible, laptops in split-screen, etc.) — the dialog looks fine
 * on a big monitor and unusable on a laptop.
 *
 * `min-h-0` is the non-obvious part: without it, `flex-1` reports the
 * intrinsic content height as the minimum and the scroll region never
 * shrinks below full content — so nothing actually scrolls. `min-h-0`
 * lets the flex child shrink below its intrinsic height, which enables
 * the `overflow-y-auto` to kick in.
 *
 * Negative horizontal margin + matching horizontal padding pulls the
 * scrollbar to the DialogContent edge instead of leaving a 24 px gutter
 * of dead space to the right of it.
 */
export function DialogBody({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('-mx-6 min-h-0 flex-1 overflow-y-auto px-6', className)}>{children}</div>
  );
}

export const DialogTitle = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('font-heading text-lg font-semibold leading-tight text-ink', className)}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-ink-muted', className)}
      {...props}
    />
  );
});
